import {syncSenderSurprisePanel} from '@theme/pb-sender-surprise';
/**
 * PB-FOUNDATION validation coordinator — single validateActiveStep delegates to field registry.
 * PB-FND-31: extract validateButtonGrid / validateStep / checkStepValidity from pb-logic-core.
 */

import {
  buildFieldCtx,
  buildSubFieldCtx,
  buildVariantFieldFromOption,
  FIELD_MODES,
  findStepElementForField,
  getDefaultFieldRegistry,
  isUnifiedFieldEngineEnabled,
  parseSettings as parseSharedFieldSettings,
  validateChoiceGrid,
  validateTopicAnswerStep,
  validateTopicMultiGate,
} from '@theme/pb-field-runtime';
import { getFieldReadiness as getAiImageFieldReadiness, resolveAiFinalizeReadyForSavedField } from '@theme/pb-field-ai-image';
import { getFieldReadiness as getAiTextFieldReadiness } from '@theme/pb-field-ai-text';
import { computeAiTextReadiness, parseAiTextFieldSettings } from '@theme/pb-ai-text-logic';
import {
  blocksForwardToOffersAddonsDoorway,
  classifyNavigationIntent,
  getOffersDoorwayBlockReason,
  getSubLeaveBlockReason,
} from '@theme/pb-ai-session-gate';
import {
  purgeOrphanAiImageByLabel,
  resolveMainProductAiLabels,
  resolveSubFlowAiLabels,
} from '@theme/pb-ai-catalog-scope';
import { resolveNavContextFromBuilderState } from '@theme/pb-cart-child-edit';
import { reportFinishBlock } from '@theme/pb-finish-beacon';
import { startFreshFromStuckFinalize } from '@theme/pb-draft-persistence';

const FINALIZE_MSG_STILL_CREATING = 'Still creating your character — hang tight.';
const FINALIZE_MSG_GENERIC = 'Complete required steps before finishing.';
const FINALIZE_MSG_BUNDLE_META =
  'Finish is waiting on an old photo job — tap Start fresh or go back to personalize.';

function isAiJobRunning(saved) {
  if (!saved) return false;
  return (
    saved.isGenerating === true ||
    saved.jobStatus === 'running' ||
    saved.jobStatus === 'queued'
  );
}

function detectStaleBundleAiMetadata(builderState, builderData) {
  if (!builderState) return false;
  const data = builderData || (typeof window !== 'undefined' ? window.BUILDER_DATA : null);
  const mainLabels = resolveMainProductAiLabels(data);
  const mainByLabel = builderState.aiImageByLabel?.byLabel;
  if (mainByLabel) {
    for (const label of Object.keys(mainByLabel)) {
      if (!mainLabels.has(label) && isAiJobRunning(mainByLabel[label])) return true;
    }
  }
  const subByLabel = builderState.sub?.aiImageByLabel?.byLabel;
  if (subByLabel) {
    const subActive = builderState.mode === 'SUB' && builderState.sub?.isQueueItem;
    const subLabels = subActive ? resolveSubFlowAiLabels(builderState) : new Set();
    for (const label of Object.keys(subByLabel)) {
      if ((!subActive || !subLabels.has(label)) && isAiJobRunning(subByLabel[label])) {
        return true;
      }
    }
  }
  return false;
}

function parseStepIndexFromStepPage(stepPage) {
  if (!stepPage || typeof stepPage !== 'object') return undefined;
  let el = stepPage;
  while (el) {
    const id = el.id || '';
    const match = id.match(/content-page-(\d+)$/);
    if (match) return parseInt(match[1], 10);
    el = el.parentElement;
  }
  return undefined;
}

function resolveShopifyBundleInheritApi() {
  return typeof window !== 'undefined' && window.PBShopifyBundleInherit
    ? window.PBShopifyBundleInherit
    : null;
}

function resolvePlannedFieldsFromBuilderData(builderData, pb) {
  if (builderData?.shopifyBundle === true && Array.isArray(builderData.shopifyBundleMergedPersonalization)) {
    return builderData.shopifyBundleMergedPersonalization;
  }
  if (typeof pb?.getPersonalizationFields === 'function') {
    const item = builderData?.mainProduct || {};
    return pb.getPersonalizationFields(item) || [];
  }
  const config = builderData?.mainProduct?.config || builderData?.mainProduct?.personalization || [];
  return Array.isArray(config) ? config : [];
}

/**
 * PB-MF-2 — short label by default; mixed collision adds product title.
 * @param {string} label
 * @param {object} [field]
 * @param {object} [builderData]
 * @param {object} [pb]
 */
function customerMessageForField(label, field, builderData, pb) {
  const name = String(label || 'this field').trim() || 'this field';
  const mergeMode = resolveShopifyBundleMergeMode(builderData);
  if (mergeMode === 'mixed' && field) {
    const inherit = resolveShopifyBundleInheritApi();
    if (inherit?.collectShopifyBundleCollidingLabels && inherit?.normalizeShopifyBundleFieldLabel) {
      const fields = resolvePlannedFieldsFromBuilderData(builderData, pb);
      const colliding = inherit.collectShopifyBundleCollidingLabels(fields);
      const norm = inherit.normalizeShopifyBundleFieldLabel(field.label);
      if (colliding.has(norm)) {
        const productTitle = field.provenance?.title && String(field.provenance.title).trim();
        if (productTitle) return `Complete ${name} (${productTitle}) before finishing.`;
      }
    }
  }
  return `Complete ${name} before finishing.`;
}

/**
 * PB-MF-2 — Start fresh only for broken-session blockers (stale AI metadata).
 * @param {string|null|undefined} blockedBy
 * @returns {boolean}
 */
export function isBrokenSessionFinalizeBlocker(blockedBy) {
  return blockedBy === 'bundle_meta';
}

function customerMessageForTopicParent(label) {
  const name = String(label || 'topics').trim() || 'topics';
  return `Choose your topics on ${name} before finishing.`;
}

function customerMessageForTopicAnswer(label) {
  const name = String(label || 'topic').trim() || 'topic';
  return `Complete your ${name} details before finishing.`;
}

function resolveShopifyBundleMergeMode(builderData) {
  if (!builderData || builderData.shopifyBundle !== true) return null;
  return builderData.shopifyBundleMergeMode === 'mixed' ? 'mixed' : 'pack';
}

function fieldTypeLower(field) {
  return String(field?.type || '').toLowerCase();
}

function isAiImageField(field) {
  return fieldTypeLower(field).includes('ai image');
}

function isAiTextField(field) {
  return fieldTypeLower(field).includes('ai text');
}

function escapeDomLabel(value) {
  const str = String(value ?? '');
  if (typeof CSS !== 'undefined' && typeof CSS.escape === 'function') return CSS.escape(str);
  return str.replace(/\\/g, '\\\\').replace(/"/g, '\\"');
}

function resolveFieldSelectionLookup(field, selections, builderData) {
  const mergeMode = resolveShopifyBundleMergeMode(builderData);
  const inherit = resolveShopifyBundleInheritApi();
  if (mergeMode && typeof inherit?.resolveShopifyBundleMediaSelectionLookup === 'function') {
    if (isMediaAnswerField(field)) {
      return inherit.resolveShopifyBundleMediaSelectionLookup(field, selections, mergeMode);
    }
    if (typeof inherit.resolveShopifyBundleSelectionLookup === 'function') {
      return inherit.resolveShopifyBundleSelectionLookup(field, selections, mergeMode);
    }
  }
  if (mergeMode && typeof inherit?.resolveShopifyBundleSelectionLookup === 'function') {
    return inherit.resolveShopifyBundleSelectionLookup(field, selections, mergeMode);
  }
  const displayLabel = field?.displayLabel || field?.label || 'field';
  const selectionKey = field?.label || '';
  const sel = selections || {};
  const hasKey = Object.prototype.hasOwnProperty.call(sel, selectionKey);
  return {
    selectionKey,
    displayLabel,
    hasKey,
    value: hasKey ? sel[selectionKey] : undefined,
  };
}

function resolveFieldIdentityKeys(field, builderData) {
  const mergeMode = resolveShopifyBundleMergeMode(builderData);
  const inherit = resolveShopifyBundleInheritApi();
  if (mergeMode && typeof inherit?.resolveShopifyBundleFieldIdentityKeys === 'function') {
    return inherit.resolveShopifyBundleFieldIdentityKeys(field, mergeMode);
  }
  const label = field?.label != null ? String(field.label).trim() : '';
  return label ? [label] : [];
}

function isUploadField(field) {
  return fieldTypeLower(field).includes('upload');
}

function isAudioField(field) {
  return fieldTypeLower(field).includes('audio');
}

function isMediaAnswerField(field) {
  return isUploadField(field) || isAudioField(field) || isAiImageField(field);
}

function selectionValueHasAnswer(value) {
  if (value == null) return false;
  if (Array.isArray(value)) {
    return value.some((entry) => {
      if (entry == null) return false;
      if (typeof entry === 'object') return selectionValueHasAnswer(entry);
      return String(entry).trim() !== '';
    });
  }
  if (typeof value === 'object') {
    return Object.keys(value).some((key) => {
      const nested = value[key];
      return nested != null && String(nested).trim() !== '';
    });
  }
  return String(value).trim() !== '';
}

function resolveAiImageSavedEntry(field, builderState, builderData) {
  const byLabel =
    builderState?.mode === 'SUB' && builderState.sub?.isQueueItem
      ? builderState.sub?.aiImageByLabel?.byLabel
      : builderState?.aiImageByLabel?.byLabel;
  if (!byLabel || typeof byLabel !== 'object') return null;
  const keys = resolveFieldIdentityKeys(field, builderData);
  for (let i = 0; i < keys.length; i += 1) {
    const saved = byLabel[keys[i]];
    if (saved && typeof saved === 'object') return saved;
  }
  return null;
}

function findDomNodeForFieldIdentityKeys(root, selector, keys) {
  if (!root || typeof root.querySelector !== 'function' || !keys?.length) return null;
  for (let i = 0; i < keys.length; i += 1) {
    const key = keys[i];
    if (!key) continue;
    const node = root.querySelector(`${selector}[data-label="${escapeDomLabel(key)}"]`);
    if (node) return node;
  }
  return null;
}

function domMediaFieldIsValid(node) {
  if (!node) return false;
  const hidden = node.querySelector?.('input.builder-required[data-label]');
  if (hidden && hidden.dataset?.required === 'true') {
    return hidden.dataset.valid === 'true';
  }
  if (hidden && hidden.dataset?.valid === 'true') return true;
  const val = hidden?.value != null ? String(hidden.value).trim() : '';
  if (!val || val === '[]') return false;
  try {
    const parsed = JSON.parse(val);
    return selectionValueHasAnswer(parsed);
  } catch (e) {
    return selectionValueHasAnswer(val);
  }
}

function classifyStepPageBlocker(stepPage, field, options = {}) {
  const stepIndex = parseStepIndexFromStepPage(stepPage);
  if (stepPage?.dataset?.topicMulti === 'true') {
    const label = stepPage.dataset.topicLabel || field?.label || 'topics';
    return {
      blockedBy: `topic_parent:${label}`,
      customerMessage: customerMessageForTopicParent(label),
      stepIndex,
    };
  }
  if (stepPage?.dataset?.topicParent) {
    const answerLabel = stepPage.dataset.topicValue || field?.label || 'topic';
    return {
      blockedBy: `topic_answer:${answerLabel}`,
      customerMessage: customerMessageForTopicAnswer(answerLabel),
      stepIndex,
    };
  }
  const label =
    field?.label ||
    stepPage?.querySelector?.('[data-label]')?.dataset?.label ||
    'field';
  const { reason, length } = resolveTextBlockReasonFromState(field, options);
  return {
    blockedBy: `field:${label}`,
    blockedReason: reason || undefined,
    ...(reason ? lengthDiagnostics(length, parseFieldSettings(field, options.pb)) : {}),
    customerMessage: reason
      ? customerMessageForFieldReason(
          label,
          field,
          options.builderData,
          options.pb,
          reason,
          length,
          parseFieldSettings(field, options.pb)
        )
      : customerMessageForField(label, field, options.builderData, options.pb),
    stepIndex,
  };
}

/**
 * PB-FGP-2 — when the DOM says a text step is invalid, read the saved answer so the
 * customer hears the real reason (too short / too long) instead of "Complete X".
 * @returns {{ reason: 'empty'|'too_short'|'too_long'|null, length: number }}
 */
function resolveTextBlockReasonFromState(field, options = {}) {
  if (!field || !isTextAnswerField(field)) return { reason: null, length: 0 };
  const s = options.builderState;
  const stateSlice = s?.mode === 'SUB' ? s?.sub : s?.main;
  const selections = stateSlice?.selections;
  if (!selections) return { reason: null, length: 0 };
  const settings = parseFieldSettings(field, options.pb);
  if (!settings?.required) return { reason: null, length: 0 };
  const lookup = resolveFieldSelectionLookup(field, selections, options.builderData);
  const val = lookup.hasKey ? String(lookup.value ?? '').trim() : '';
  const hasAudio = hasAudioCompanionAnswer(field, settings, selections, options.pb);
  return { reason: resolveTextLengthReason(val, settings, hasAudio), length: val.length };
}

function resolveAiBlockFromSummaries(summaries, message) {
  const rows = summaries || [];
  const running = rows.find((row) => row.jobRunning);
  if (running) {
    return {
      blockedBy: `ai_image:${running.fieldLabel || 'character'}`,
      customerMessage: message,
      stepIndex: running.stepIndex ?? undefined,
    };
  }
  const review = rows.find((row) => row.needsReview);
  if (review) {
    return {
      blockedBy: `ai_image:${review.fieldLabel || 'character'}`,
      customerMessage: message,
      stepIndex: review.stepIndex ?? undefined,
    };
  }
  const notReady = rows.find((row) => !row.finalizeReady);
  if (notReady) {
    return {
      blockedBy: `ai_image:${notReady.fieldLabel || 'character'}`,
      customerMessage: message,
      stepIndex: notReady.stepIndex ?? undefined,
    };
  }
  return { blockedBy: 'ai_image:unknown', customerMessage: message };
}

function notReadyResult(partial) {
  return {
    ready: false,
    blockedBy: partial.blockedBy || null,
    blockedReason: partial.blockedReason,
    answerLength: partial.answerLength,
    minLength: partial.minLength,
    maxLength: partial.maxLength,
    customerMessage: partial.customerMessage || FINALIZE_MSG_GENERIC,
    stepIndex: partial.stepIndex,
  };
}

function readyResult() {
  return { ready: true, blockedBy: null, customerMessage: null };
}

/** PB-FGD-4 — Continue validates active step only; Finish scans all required fields. */
export const READINESS_SCOPE = Object.freeze({
  ACTIVE: 'active',
  FULL: 'full',
});

/**
 * PB-FGP-1 — one rule source. Continue (field runtime) and Finish (here) must read
 * `required` / `min` / `max` / `recorder` identically, or a customer passes every
 * Continue and dead-ends on Finish. `pb.parseSettings` IS the shared parser at
 * runtime; the fallback keeps that true in tests and off-page callers.
 */
function parseFieldSettings(field, pb) {
  if (typeof pb?.parseSettings === 'function') return pb.parseSettings(field?.settings);
  return parseSharedFieldSettings(field?.settings);
}

function isTextAnswerField(field) {
  const type = fieldTypeLower(field);
  return type.includes('text') && !type.includes('ai text');
}

function audioCompanionLabel(field, pb) {
  const label = field?.label != null ? String(field.label).trim() : '';
  if (!label) return '';
  if (typeof pb?.audioPropertyLabel === 'function') return pb.audioPropertyLabel(label);
  return topicAudioLabel(label);
}

/** Mirrors `validateTextInput`: a valid voice note stands in for the text minimum. */
function hasAudioCompanionAnswer(field, settings, selections, pb) {
  if (!settings?.recorder) return false;
  const key = audioCompanionLabel(field, pb);
  if (!key) return false;
  return selectionValueHasAnswer(selections?.[key]);
}

/**
 * PB-FGP-1 — same verdict order as `validateTextInput` (max always applies; min is
 * waived by a voice note).
 * @returns {'empty'|'too_short'|'too_long'|null}
 */
function resolveTextLengthReason(val, settings, hasAudio) {
  const min = Number(settings?.min) > 0 ? Number(settings.min) : 0;
  const max = Number(settings?.max) > 0 ? Number(settings.max) : Infinity;
  if (!val) return hasAudio ? null : 'empty';
  if (val.length > max) return 'too_long';
  if (!hasAudio && val.length < min) return 'too_short';
  return null;
}

/**
 * PB-FGP-3 — numeric-only blocker detail for the diagnostic snapshot / beacon.
 * Lengths, never content.
 */
function lengthDiagnostics(len, settings) {
  const out = {};
  if (Number.isFinite(len)) out.answerLength = len;
  const min = Number(settings?.min);
  const max = Number(settings?.max);
  if (Number.isFinite(min) && min > 0) out.minLength = min;
  if (Number.isFinite(max) && max > 0) out.maxLength = max;
  return out;
}

/** PB-FGP-2 — "Complete X" is wrong for a too-long answer; say the actual reason. */
function customerMessageForFieldReason(label, field, builderData, pb, reason, len, settings) {
  const name = String(label || 'this field').trim() || 'this field';
  if (isTextAnswerField(field)) {
    if (reason === 'too_short') {
      const min = Number(settings?.min) || 0;
      return `Your ${name} needs at least ${min} characters — you have ${len}.`;
    }
    if (reason === 'too_long') {
      const max = Number(settings?.max) || 0;
      return `Your ${name} is too long — ${len} characters, the limit is ${max}.`;
    }
  }
  return customerMessageForField(label, field, builderData, pb);
}

function topicAudioLabel(textLabel) {
  return String(textLabel || '').trim() + ' (audio)';
}

/**
 * State-first field readiness — DOM fallback when state slice is empty.
 * @returns {{ decided: boolean, ready?: boolean, blockedBy?: string, customerMessage?: string, stepIndex?: number }}
 */
function evaluateFieldReadinessFromState(field, stateSlice, pb, options = {}) {
  if (!field || !stateSlice) return { decided: false };
  if (fieldTypeLower(field) === 'variant') return { decided: false };
  const settings = parseFieldSettings(field, pb);
  if (!settings.required) return { decided: true, ready: true };

  const selections = stateSlice.selections || {};
  const topicSelections = stateSlice.topicSelections || {};

  if (String(field.type || '').includes('TopicMulti')) {
    const topicLabel = field.label || 'field';
    if (!Object.prototype.hasOwnProperty.call(topicSelections, topicLabel)) {
      if (options.treatMissingSelectionAsEmpty) {
        return {
          decided: true,
          ready: false,
          blockedBy: `topic_parent:${topicLabel}`,
          customerMessage: customerMessageForTopicParent(topicLabel),
        };
      }
      return { decided: false };
    }
    const selected = Array.isArray(topicSelections[topicLabel]) ? topicSelections[topicLabel] : [];
    const min = settings.min > 0 ? settings.min : 1;
    const max = settings.max > 0 && settings.max < Infinity ? settings.max : Infinity;
    const count = selected.filter((t) => String(t || '').trim()).length;
    if (count >= min && count <= max) return { decided: true, ready: true };
    return {
      decided: true,
      ready: false,
      blockedBy: `topic_parent:${topicLabel}`,
      customerMessage: customerMessageForTopicParent(topicLabel),
    };
  }

  const lookup = resolveFieldSelectionLookup(field, selections, options.builderData);
  const messageLabel = lookup.displayLabel || 'field';

  if (isMediaAnswerField(field)) {
    const hasSelectionAnswer = lookup.hasKey && selectionValueHasAnswer(lookup.value);
    const savedAi = isAiImageField(field)
      ? resolveAiImageSavedEntry(field, options.builderState, options.builderData)
      : null;
    const savedAiReady =
      savedAi &&
      resolveAiFinalizeReadyForSavedField(
        field,
        savedAi,
        resolveReviewAcknowledgedForField(field, null, options.builderData)
      );
    if (hasSelectionAnswer || savedAiReady) {
      return { decided: true, ready: true };
    }
    if (typeof document !== 'undefined') {
      const containerSelector = options.containerSelector || '#main-flow-container';
      const root = document.querySelector(containerSelector);
      const keys = resolveFieldIdentityKeys(field, options.builderData);
      const uploadWrap = findDomNodeForFieldIdentityKeys(root, '.pb-upload-wrap', keys);
      if (domMediaFieldIsValid(uploadWrap)) {
        return { decided: true, ready: true };
      }
      const aiWrap = findDomNodeForFieldIdentityKeys(root, '.pb-ai-image-wrap', keys);
      if (aiWrap) {
        const reviewAcknowledged = resolveReviewAcknowledgedForField(
          field,
          aiWrap,
          options.builderData
        );
        const readiness =
          typeof window !== 'undefined' && window.PBAiImage?.getFieldReadiness
            ? window.PBAiImage.getFieldReadiness(aiWrap, { reviewAcknowledged })
            : getAiImageFieldReadiness(aiWrap, { reviewAcknowledged });
        if (readiness.finalizeReady) return { decided: true, ready: true };
        if (options.treatMissingSelectionAsEmpty) {
          return {
            decided: true,
            ready: false,
            blockedBy: `ai_image:${messageLabel}`,
            customerMessage: customerMessageForField(
              messageLabel,
              field,
              options.builderData,
              pb
            ),
          };
        }
      }
      const audioWrap = findDomNodeForFieldIdentityKeys(root, '.pb-audio-wrap', keys);
      if (domMediaFieldIsValid(audioWrap)) {
        return { decided: true, ready: true };
      }
    }
    if (!lookup.hasKey && !savedAi) {
      if (options.treatMissingSelectionAsEmpty) {
        return {
          decided: true,
          ready: false,
          blockedBy: isAiImageField(field) ? `ai_image:${messageLabel}` : `field:${messageLabel}`,
          customerMessage: customerMessageForField(
            messageLabel,
            field,
            options.builderData,
            pb
          ),
        };
      }
      return { decided: false };
    }
    if (!hasSelectionAnswer && !savedAiReady) {
      return {
        decided: true,
        ready: false,
        blockedBy: isAiImageField(field) ? `ai_image:${messageLabel}` : `field:${messageLabel}`,
        customerMessage: customerMessageForField(
          messageLabel,
          field,
          options.builderData,
          pb
        ),
      };
    }
  }

  const hasAudio = hasAudioCompanionAnswer(field, settings, selections, pb);

  const blockForReason = (reason, len) => ({
    decided: true,
    ready: false,
    blockedBy: `field:${messageLabel}`,
    blockedReason: reason,
    ...lengthDiagnostics(len, settings),
    customerMessage: customerMessageForFieldReason(
      messageLabel,
      field,
      options.builderData,
      pb,
      reason,
      len,
      settings
    ),
  });

  if (!lookup.hasKey) {
    if (hasAudio) return { decided: true, ready: true };
    if (options.treatMissingSelectionAsEmpty) return blockForReason('empty', 0);
    return { decided: false };
  }
  const val = String(lookup.value ?? '').trim();
  // Choice min/max describe selection counts; only text answers have character limits.
  const reason = isTextAnswerField(field)
    ? resolveTextLengthReason(val, settings, hasAudio)
    : val || hasAudio ? null : 'empty';
  if (reason) return blockForReason(reason, val.length);
  return { decided: true, ready: true };
}

/** Sync Topic Multi parent checkboxes from saved topicSelections before DOM validate. */
function hydrateTopicMultiParentFromState(stepPage, stateSlice) {
  if (!stepPage || stepPage.dataset?.topicMulti !== 'true' || !stateSlice) return;
  const fieldLabel = stepPage.dataset.topicLabel || '';
  const selected = Array.isArray(stateSlice.topicSelections?.[fieldLabel])
    ? stateSlice.topicSelections[fieldLabel]
    : [];
  if (!selected.length) return;
  const grid = stepPage.querySelector?.('[data-topic-list]');
  if (!grid) return;
  grid.querySelectorAll('.pb-topic-checkbox').forEach((cb) => {
    const template = cb.dataset?.pbTemplate || cb.dataset?.value || '';
    cb.checked =
      selected.includes(template) ||
      (cb.dataset?.value && selected.includes(cb.dataset.value));
  });
  validateChoiceGrid(grid);
}

function resolveFieldForActiveStep(stepPage, pb, stateSlice, mode) {
  if (!stepPage) return null;
  const entry = typeof pb?.getSubStepEntry === 'function' ? pb.getSubStepEntry(stepPage) : null;
  if (entry?.type === 'config' && entry.field) return entry.field;
  if (entry?.type === 'variant' && entry.option) return buildVariantFieldFromOption(entry.option);
  if (stepPage.dataset?.topicMulti === 'true') {
    return { type: 'TopicMulti', label: stepPage.dataset.topicLabel || '' };
  }
  const mountEl = stepPage.querySelector?.('[data-pb-field-mount]');
  if (mountEl) {
    const field = parseMountField(mountEl);
    if (field) return field;
  }
  if (stepPage.querySelector?.('.variant-btn, .variant-slider-track')) {
    return buildVariantFieldFromOption({ name: '', values: [] });
  }
  return null;
}

function validateStepWithStateFirst(stepPage, field, stateSlice, pb, validateStepFn, options = {}) {
  if (stepPage?.dataset?.topicParent) return validateTopicAnswerStep(stepPage);
  // A saved design label does not mean its mounted picker has finished loading.
  // Use the same adapter decision as Continue's click handler.
  if (stepPage && fieldTypeLower(field).replace(/\s+/g, '') === 'designlibrary' && typeof validateStepFn === 'function') {
    return validateStepFn(stepPage);
  }
  if (field) {
    const stateResult = evaluateFieldReadinessFromState(field, stateSlice, pb, options);
    if (stateResult.decided && stateResult.ready) return true;
    // State missing or stale on Finish — mounted step DOM is authoritative when present.
  }
  hydrateTopicMultiParentFromState(stepPage, stateSlice);
  if (typeof validateStepFn === 'function') return validateStepFn(stepPage);
  return true;
}

function stateFirstBlockFromStep(stepPage, field, stateSlice, pb, options = {}) {
  if (stepPage?.dataset?.topicParent) {
    return validateTopicAnswerStep(stepPage) ? null : classifyStepPageBlocker(stepPage, field, options);
  }
  if (field) {
    const stateResult = evaluateFieldReadinessFromState(field, stateSlice, pb, options);
    if (stateResult.decided && !stateResult.ready) {
      return {
        blockedBy: stateResult.blockedBy,
        blockedReason: stateResult.blockedReason,
        answerLength: stateResult.answerLength,
        minLength: stateResult.minLength,
        maxLength: stateResult.maxLength,
        customerMessage: stateResult.customerMessage,
        stepIndex: stateResult.stepIndex ?? parseStepIndexFromStepPage(
          findStepPageForField(
            field,
            resolveFlowContainerSelector(FIELD_MODES.MAIN),
            options.builderData
          ) ||
            findStepPageForField(
              field,
              resolveFlowContainerSelector(FIELD_MODES.SUB),
              options.builderData
            )
        ),
      };
    }
  }
  return null;
}

/**
 * Count AI review scopes for diagnostic snapshot (no labels / PII).
 * @param {object} [builderState]
 * @returns {{ aiSummariesTotal: number, aiSummariesRunning: number }}
 */
function buildFinishScopeCounts(builderState) {
  const counts = { aiSummariesTotal: 0, aiSummariesRunning: 0 };
  const s = builderState;
  if (!s) return counts;
  const hop = typeof window !== 'undefined' ? window.PBAiReviewHop : null;
  if (!hop?.collectAllAiSummaries) return counts;
  const summaries = hop.collectAllAiSummaries(s);
  counts.aiSummariesTotal = summaries.length;
  counts.aiSummariesRunning = summaries.filter((row) => row.jobRunning).length;
  return counts;
}

/**
 * PB-FGD-3 — structured Finish diagnostic snapshot (no PII / free text).
 * @param {object} [builderState]
 * @param {{ pb?: object, builderData?: object }} [options]
 * @returns {{ ready: boolean, blockedBy: string|null, step: number|null, maxSteps: number|null, draftResume: boolean, scopeCounts: { aiSummariesTotal: number, aiSummariesRunning: number }, stepIndex?: number }}
 */
export function getFinishDiagnosticSnapshot(builderState, options = {}) {
  const s = builderState || (typeof window !== 'undefined' ? window.builderState : null);
  const stateSlice = s?.mode === 'SUB' ? s?.sub : s?.main;
  const pb = options.pb || (typeof window !== 'undefined' ? window.productBuilder : null);
  const coord = pb?._pbValidation;
  const readiness = resolveFinalizeReadiness(s, stateSlice, {
    pb,
    validateStep: coord?.validateStep ? (page) => coord.validateStep(page) : undefined,
    builderData: options.builderData,
  });
  const snapshot = {
    ready: readiness.ready,
    blockedBy: readiness.blockedBy,
    step: stateSlice?.step ?? null,
    maxSteps: stateSlice?.maxSteps ?? null,
    draftResume: !!s?._pbDraftResumeSession,
    scopeCounts: buildFinishScopeCounts(s),
  };
  if (readiness.stepIndex != null) snapshot.stepIndex = readiness.stepIndex;
  // PB-FGP-3 — codes and lengths only; never the answer itself.
  if (readiness.blockedReason) snapshot.blockedReason = readiness.blockedReason;
  if (readiness.answerLength != null) snapshot.answerLength = readiness.answerLength;
  if (readiness.minLength != null) snapshot.minLength = readiness.minLength;
  if (readiness.maxLength != null) snapshot.maxLength = readiness.maxLength;
  return snapshot;
}

/**
 * PB-FGD-4 — unified state-first readiness resolver (Continue + Finish parity).
 * @param {object} [builderState]
 * @param {object} [ctx] — main or sub state slice
 * @param {{ pb?: object, validateStep?: (stepPage: Element) => boolean, builderData?: object, scope?: 'active'|'full' }} [options]
 * @returns {{ ready: boolean, blockedBy: string|null, customerMessage: string|null, stepIndex?: number }}
 */
export function resolveStepReadiness(builderState, ctx, options = {}) {
  const scope = options.scope || READINESS_SCOPE.FULL;
  const s = builderState || (typeof window !== 'undefined' ? window.builderState : null);
  if (!s) return readyResult();

  const builderData = options.builderData ?? (typeof window !== 'undefined' ? window.BUILDER_DATA : null);
  if (scope === READINESS_SCOPE.FULL && detectStaleBundleAiMetadata(s, builderData)) {
    return notReadyResult({
      blockedBy: 'bundle_meta',
      customerMessage: FINALIZE_MSG_BUNDLE_META,
    });
  }

  purgeOrphanAiImageByLabel(s, { builderData });
  const stateSlice = s.mode === 'SUB' ? s.sub : s.main;
  if (!stateSlice) return readyResult();
  const stepCtx =
    ctx && ctx !== stateSlice && (ctx.step != null || ctx.maxSteps != null)
      ? { ...stateSlice, ...ctx }
      : ctx || stateSlice;

  if (scope === READINESS_SCOPE.FULL) {
    const hop = typeof window !== 'undefined' ? window.PBAiReviewHop : null;
    if (hop?.collectAllAiSummaries && hop?.resolveFinalizeFooterMessage) {
      const activeScopeId =
        typeof hop.resolveActiveReviewScopeId === 'function'
          ? hop.resolveActiveReviewScopeId(s)
          : undefined;
      const summaries = hop.collectAllAiSummaries(s);
      const msg = hop.resolveFinalizeFooterMessage(summaries, { activeScopeId });
      if (msg) {
        const aiBlock = resolveAiBlockFromSummaries(summaries, msg);
        return notReadyResult(aiBlock);
      }
    }
  }

  const pb = options.pb || (typeof window !== 'undefined' ? window.productBuilder : null);
  const validateStepFn = options.validateStep;
  const mode = s.mode === 'SUB' ? FIELD_MODES.SUB : FIELD_MODES.MAIN;
  const containerSelector = resolveFlowContainerSelector(mode);
  const readinessContext = { builderData, pb, builderState: s, containerSelector };

  if (scope === READINESS_SCOPE.ACTIVE) {
    const activeStepPage = resolveActiveStepPage(stepCtx, s);
    if (!activeStepPage) return readyResult();
    const field = resolveFieldForActiveStep(activeStepPage, pb, stateSlice, mode);
    const stateBlock = stateFirstBlockFromStep(activeStepPage, field, stateSlice, pb, readinessContext);
    if (stateBlock) return notReadyResult(stateBlock);
    if (!validateStepWithStateFirst(activeStepPage, field, stateSlice, pb, validateStepFn, readinessContext)) {
      return notReadyResult(classifyStepPageBlocker(activeStepPage, field, readinessContext));
    }
    return readyResult();
  }

  // Inserted topic answers have no config entry. Use the same rendered validator
  // as Continue, and preserve their position when choosing a draft/Finish blocker.
  const root = typeof document !== 'undefined' ? document.querySelector(containerSelector) : null;
  const topicAnswers = root?.querySelectorAll?.('.pb-bottom-wrapper .pb-step-viewport .pb-step[data-topic-parent]') || [];
  const invalidTopic = Array.from(topicAnswers).find(answer => !validateTopicAnswerStep(answer));
  const topicBlock = invalidTopic ? classifyStepPageBlocker(invalidTopic, null, readinessContext) : null;
  const requiredFieldBlock = block => notReadyResult(
    topicBlock && topicBlock.stepIndex < block.stepIndex ? topicBlock : block
  );
  const fields = resolveFlowFields(pb, stateSlice, mode, builderData);

  for (let i = 0; i < fields.length; i += 1) {
    const field = fields[i];
    if (isAiImageField(field)) {
      const readiness = resolveAiReadinessForField(field, containerSelector, builderData, s);
      if (!readiness.finalizeReady) {
        const label = field.displayLabel || field.label || 'character';
        return notReadyResult({
          blockedBy: `ai_image:${label}`,
          customerMessage:
            readiness.jobStatus === 'running' || readiness.jobStatus === 'queued'
              ? FINALIZE_MSG_STILL_CREATING
              : customerMessageForField(label, field, builderData, pb),
        });
      }
      continue;
    }
    if (isAiTextField(field)) {
      const readiness = resolveAiTextReadinessForField(field, containerSelector, builderData, s);
      if (!readiness.finalizeReady) {
        const label = field.label || 'text';
        return notReadyResult({
          blockedBy: `ai_text:${label}`,
          customerMessage:
            readiness.jobStatus === 'running' || readiness.jobStatus === 'queued'
              ? `Still creating ${label} — hang tight.`
              : customerMessageForField(label),
        });
      }
      continue;
    }
    if (!fieldIsRequired(field, pb)) continue;
    const stepPage = findStepPageForField(field, containerSelector, builderData);
    if (stepPage) {
      if (!validateStepWithStateFirst(stepPage, field, stateSlice, pb, validateStepFn, readinessContext)) {
        return requiredFieldBlock(classifyStepPageBlocker(stepPage, field, readinessContext));
      }
      continue;
    }
    const stateBlock = stateFirstBlockFromStep(null, field, stateSlice, pb, {
      treatMissingSelectionAsEmpty: true,
      builderData,
      builderState: s,
      containerSelector,
    });
    if (stateBlock) return requiredFieldBlock(stateBlock);
  }

  if (topicBlock) return notReadyResult(topicBlock);

  const activeStepPage = resolveActiveStepPage(stepCtx, s);
  if (activeStepPage) {
    const activeField = resolveFieldForActiveStep(activeStepPage, pb, stateSlice, mode);
    const stateBlock = stateFirstBlockFromStep(activeStepPage, activeField, stateSlice, pb, readinessContext);
    if (stateBlock) return notReadyResult(stateBlock);
    if (!validateStepWithStateFirst(activeStepPage, activeField, stateSlice, pb, validateStepFn, readinessContext)) {
      return notReadyResult(classifyStepPageBlocker(activeStepPage, activeField, readinessContext));
    }
  }

  return readyResult();
}

function __debugReadiness(builderState, ctx, options) {
  const r = resolveStepReadiness(builderState, ctx, options);
  if (!r.ready) console.error('DEBUG readiness blocked:', r);
  return r;
}

/**
 * PB-FGD-1 — named finalize blocker resolver (no PII).
 * @param {object} [builderState]
 * @param {object} [ctx] — main or sub state slice
 * @param {{ pb?: object, validateStep?: (stepPage: Element) => boolean, builderData?: object }} [options]
 * @returns {{ ready: boolean, blockedBy: string|null, customerMessage: string|null, stepIndex?: number }}
 */
export function resolveFinalizeReadiness(builderState, ctx, options = {}) {
  return resolveStepReadiness(builderState, ctx, { ...options, scope: READINESS_SCOPE.FULL });
}

/**
 * PB-MF-2 — earliest failing MAIN step when Finish is blocked (shared with draft clamp + live jump).
 * @param {object} [builderState]
 * @param {{ builderData?: object, pb?: object, validateStep?: (stepPage: Element) => boolean, maxSteps?: number }} [deps]
 * @returns {number|null}
 */
export function resolveBlockingStepIndexForFinalize(builderState, deps = {}) {
  if (!builderState || builderState.mode !== 'MAIN') return null;

  const stateSlice = builderState.main;
  if (!stateSlice) return null;
  const maxSteps = Math.max(1, Number(deps.maxSteps ?? stateSlice.maxSteps) || 1);

  const pb = deps.pb ?? (typeof window !== 'undefined' ? window.productBuilder : null);
  const readinessOpts = {
    pb,
    validateStep: deps.validateStep,
    builderData: deps.builderData ?? (typeof window !== 'undefined' ? window.BUILDER_DATA : null),
  };

  const finalize = resolveFinalizeReadiness(builderState, stateSlice, readinessOpts);
  if (finalize.ready) return null;

  if (finalize.blockedBy === 'bundle_meta') return 1;

  if (finalize.stepIndex != null && finalize.stepIndex >= 1) {
    return finalize.stepIndex;
  }

  for (let i = 1; i <= maxSteps; i += 1) {
    const probeCtx = { ...stateSlice, step: i };
    const active = resolveStepReadiness(builderState, probeCtx, {
      ...readinessOpts,
      scope: READINESS_SCOPE.ACTIVE,
    });
    if (!active.ready) {
      return active.stepIndex != null && active.stepIndex >= 1 ? active.stepIndex : i;
    }
  }

  return Math.max(1, maxSteps - 1);
}

/**
 * PB-FGD-5 — clamp draft Continue open step when saved state is not finalize-ready.
 * @param {number} openStep
 * @param {{ builderState?: object, builderData?: object, pb?: object, validateStep?: (stepPage: Element) => boolean, maxSteps?: number }} [deps]
 * @returns {number}
 */
export function clampDraftResumeOpenStepForReadiness(openStep, deps = {}) {
  const builderState =
    deps.builderState ?? (typeof window !== 'undefined' ? window.builderState : null);
  const step = Math.max(1, Number(openStep) || 1);
  if (!builderState || builderState.mode !== 'MAIN') return step;

  const stateSlice = builderState.main;
  if (!stateSlice) return step;
  const maxSteps = Math.max(1, Number(deps.maxSteps ?? stateSlice.maxSteps) || step);
  const clampedOpen = Math.min(step, maxSteps);

  const blockingStep = resolveBlockingStepIndexForFinalize(builderState, deps);
  if (blockingStep == null) return clampedOpen;

  return Math.min(clampedOpen, blockingStep);
}

function parseMountField(mountEl) {
  if (!mountEl) return null;
  try {
    const field = JSON.parse(mountEl.getAttribute('data-field') || '{}');
    return field && field.type ? field : null;
  } catch (e) {
    return null;
  }
}

function resolveSubFlowItem(subState) {
  const s = subState || {};
  if (typeof window === 'undefined') return null;
  if (s.isQueueItem) {
    const bundle = (window.BUILDER_DATA?.bundles || []).find(
      (b) => String(b?.id) === String(window.builderState?.bundle?.selectedId)
    );
    const idx = parseInt(s.currentId, 10);
    return bundle?.items?.[idx] || null;
  }
  return s.currentId != null ? window.builderState?.addonsDB?.[s.currentId] : null;
}

function resolveActiveStepPage(ctx, builderState) {
  const s = builderState || (typeof window !== 'undefined' ? window.builderState : null);
  if (!s) return null;
  const mode = s.mode === 'SUB' ? FIELD_MODES.SUB : FIELD_MODES.MAIN;
  const containerID = mode === FIELD_MODES.MAIN ? 'main-flow-container' : 'sub-flow-container';
  const prefix = mode === FIELD_MODES.MAIN ? '' : 'sub-';
  const stepNum = ctx?.step ?? (mode === FIELD_MODES.MAIN ? s.main?.step : s.sub?.step);
  if (stepNum == null || typeof document === 'undefined') return null;
  return document.querySelector(`#${containerID} #${prefix}content-page-${stepNum}`);
}

function fieldIsRequired(field, pb) {
  if (!field) return false;
  const raw = field.settings;
  if (typeof pb?.parseSettings === 'function') {
    const parsed = pb.parseSettings(raw);
    if (parsed && parsed.required) return true;
  }
  if (raw && typeof raw === 'object' && raw.required) return true;
  const s = String(raw || '');
  return /required\s*:\s*true/i.test(s);
}

export const FOOTER_NAV_LOCK = Object.freeze({
  NONE: 'none',
  VALIDATION: 'validation',
  RECORDING: 'recording',
});

const RECORDING_NAV_HINT = 'Stop or cancel your recording first.';

let footerNavSyncCoordinator = null;

/**
 * Footer chrome reflects validation OR recording lock — never native `disabled`.
 * iOS Safari (and some desktop WebViews) can leave a button untappable after
 * disabled toggles; visual lock = class + aria-disabled; click still reaches
 * handleNextClick / moveStep which re-check validity / PBFlowLock.
 */
export function applyFooterNavButtonState(btn, state) {
  if(typeof document!=='undefined')syncSenderSurprisePanel();
  if (!btn || typeof btn.classList === 'undefined') return;
  const enabled = !!state.enabled;
  const lockKind =
    state.lockKind || (enabled ? FOOTER_NAV_LOCK.NONE : FOOTER_NAV_LOCK.VALIDATION);
  if (typeof btn.removeAttribute === 'function') btn.removeAttribute('disabled');
  btn.disabled = false;
  btn.classList.toggle('pb-nav-btn--locked', !enabled);
  btn.setAttribute('aria-disabled', enabled ? 'false' : 'true');
  if (enabled) {
    btn.removeAttribute('title');
    btn.setAttribute('data-pb-nav-lock', FOOTER_NAV_LOCK.NONE);
    return;
  }
  if (state.title) btn.setAttribute('title', state.title);
  else btn.removeAttribute('title');
  btn.setAttribute('data-pb-nav-lock', lockKind);
}

export function isRecordingNavBlocked(navOpts) {
  return (
    typeof window !== 'undefined' &&
    window.PBFlowLock &&
    typeof window.PBFlowLock.blocksActiveStepNavigation === 'function' &&
    window.PBFlowLock.blocksActiveStepNavigation(navOpts)
  );
}

export function getRecordingNavBlockMessage(navOpts) {
  if (typeof window !== 'undefined' && window.PBFlowLock && typeof window.PBFlowLock.getBlockReason === 'function') {
    const reason = window.PBFlowLock.getBlockReason(navOpts);
    if (reason && reason.message) return reason.message;
  }
  return RECORDING_NAV_HINT;
}

/**
 * Active flow footer — SUB cart edit hides #main-flow-container but MAIN footer stays in DOM first.
 * @returns {HTMLElement|null}
 */
export function resolveActiveModalFooter() {
  if (typeof document === 'undefined') return null;
  const footers = document.querySelectorAll('#product-builder-root .pb-modal-footer');
  for (let i = 0; i < footers.length; i += 1) {
    const footer = footers[i];
    if (footer.offsetParent !== null || footer.getClientRects().length > 0) {
      return footer;
    }
  }
  const s = typeof window !== 'undefined' ? window.builderState : null;
  if (s?.mode === 'SUB') {
    return document.getElementById('subNextBtn')?.closest('.pb-modal-footer') || footers[footers.length - 1] || null;
  }
  return document.getElementById('nextBtn')?.closest('.pb-modal-footer') || footers[0] || null;
}

/**
 * Plain hint below footer when customer taps dimmed Continue/Back during recording.
 */
export function showNavBlockHint(message) {
  if (typeof document === 'undefined') return;
  const footer = resolveActiveModalFooter();
  if (!footer || !footer.parentElement) return;
  let hint = footer.parentElement.querySelector('.pb-nav-block-hint');
  if (!hint) {
    hint = document.createElement('p');
    hint.className = 'pb-nav-block-hint';
    hint.setAttribute('aria-live', 'polite');
    hint.setAttribute('role', 'status');
    footer.parentElement.insertBefore(hint, footer);
  }
  const text = message ? String(message) : '';
  hint.textContent = text;
  hint.classList.toggle('pb-nav-block-hint--active', !!text);
  hint.style.display = text ? 'block' : 'none';
  if (text && typeof hint.scrollIntoView === 'function') {
    try {
      hint.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    } catch (_e) {
      hint.scrollIntoView(false);
    }
  }
}

export function clearNavBlockHint() {
  showNavBlockHint('');
  syncStartFreshAffordance(false);
}

let startFreshAffordanceBound = false;

function resolveStartFreshProductId() {
  const data = typeof window !== 'undefined' ? window.BUILDER_DATA : null;
  return data?.mainProduct?.id ?? null;
}

function ensureStartFreshAffordanceEl() {
  const footer = resolveActiveModalFooter();
  if (!footer?.parentElement) return null;
  let wrap = footer.parentElement.querySelector('.pb-start-fresh-wrap');
  if (wrap) return wrap;

  wrap = document.createElement('div');
  wrap.className = 'pb-start-fresh-wrap';
  wrap.style.cssText = 'display:none;margin:0 0 8px;text-align:center;';

  const btn = document.createElement('button');
  btn.type = 'button';
  btn.className = 'pb-start-fresh-btn';
  btn.textContent = 'Start fresh';
  btn.style.cssText =
    'padding:10px 16px;border:1px solid #ccc;border-radius:8px;background:#fff;color:#222;font-size:0.95rem;cursor:pointer;';
  wrap.appendChild(btn);

  const hint = footer.parentElement.querySelector('.pb-nav-block-hint');
  if (hint?.nextSibling) {
    footer.parentElement.insertBefore(wrap, hint.nextSibling);
  } else {
    footer.parentElement.insertBefore(wrap, footer);
  }

  if (!startFreshAffordanceBound) {
    startFreshAffordanceBound = true;
    btn.addEventListener('click', onStartFreshFromStuckFinalizeClick);
  }
  return wrap;
}

function onStartFreshFromStuckFinalizeClick() {
  const productId = resolveStartFreshProductId();
  const started = startFreshFromStuckFinalize(productId, {
    reconcileDom: () => {
      if (
        typeof window !== 'undefined' &&
        window.productBuilder &&
        typeof window.productBuilder.reconcileStepDom === 'function'
      ) {
        window.productBuilder.reconcileStepDom();
      }
    },
    scheduleFooterSync: scheduleFooterNavSync,
  });
  if (!started) return;
  syncStartFreshAffordance(false);
  clearNavBlockHint();
  scheduleFooterNavSync();
}

/**
 * Show Start fresh when Finish is blocked on last MAIN step (PB-FGD-2).
 * @param {boolean} visible
 */
export function syncStartFreshAffordance(visible) {
  if (typeof document === 'undefined') return;
  if (!visible) {
    const footer = resolveActiveModalFooter();
    const wrap = footer?.parentElement?.querySelector('.pb-start-fresh-wrap');
    if (wrap) wrap.style.display = 'none';
    return;
  }
  const wrap = ensureStartFreshAffordanceEl();
  if (wrap) wrap.style.display = '';
}

/**
 * Footer hint when finalize is blocked (AI job running / review pending / named field).
 */
export function getFinalizeBlockMessage(builderState) {
  const pb = typeof window !== 'undefined' ? window.productBuilder : null;
  const coord = pb?._pbValidation;
  const result = resolveFinalizeReadiness(builderState, undefined, {
    pb,
    validateStep: coord?.validateStep ? (page) => coord.validateStep(page) : undefined,
  });
  if (result.ready) return FINALIZE_MSG_GENERIC;
  return result.customerMessage || FINALIZE_MSG_GENERIC;
}

/**
 * PB-MF-2 — show blocker hint and jump to earliest failing step on MAIN Finish block.
 * @param {object} [pb]
 * @param {object} [ctx] — main or sub state slice
 */
export function blockFinalizeWithHintAndJump(pb, ctx) {
  const builderState = typeof window !== 'undefined' ? window.builderState : null;
  const stateSlice =
    ctx || (builderState?.mode === 'SUB' ? builderState?.sub : builderState?.main);
  const syncPb = pb || (typeof window !== 'undefined' ? window.productBuilder : null);
  const coord = syncPb?._pbValidation;

  showNavBlockHint(getFinalizeBlockMessage(builderState));

  // PB-FGP-3 — a blocked Finish is otherwise invisible: it only ever happens on the
  // customer's device. One PII-free record per distinct blocker per session.
  try {
    reportFinishBlock(
      getFinishDiagnosticSnapshot(builderState, {
        pb: syncPb,
        builderData: typeof window !== 'undefined' ? window.BUILDER_DATA : null,
      }),
      { builderState }
    );
  } catch (e) {
    /* diagnostics must never break the customer's flow */
  }

  if (builderState?.mode === 'MAIN' && !builderState?.cartEdit && stateSlice) {
    const jumpStep = resolveBlockingStepIndexForFinalize(builderState, {
      pb: syncPb,
      validateStep: coord?.validateStep ? (page) => coord.validateStep(page) : undefined,
      builderData: typeof window !== 'undefined' ? window.BUILDER_DATA : null,
    });
    if (
      jumpStep != null &&
      jumpStep >= 1 &&
      jumpStep < (stateSlice.step ?? jumpStep) &&
      typeof syncPb?.jumpToMainStep === 'function'
    ) {
      syncPb.jumpToMainStep(jumpStep);
    }
  }

  if (syncPb && typeof syncPb.checkStepValidity === 'function') syncPb.checkStepValidity();
}

/**
 * PB-AIG-3 — enforce validateFinalizeReady before any save/finalize path.
 * @param {object} [pb]
 * @param {object} [ctx] — main or sub state slice
 * @returns {boolean} true when finalize may proceed
 */
export function attemptFinalize(pb, ctx) {
  const builderState = typeof window !== 'undefined' ? window.builderState : null;
  const stateSlice =
    ctx || (builderState?.mode === 'SUB' ? builderState?.sub : builderState?.main);
  const validate =
    (pb && typeof pb.validateFinalizeReady === 'function' && pb.validateFinalizeReady.bind(pb)) ||
    (typeof window !== 'undefined' &&
      window.productBuilder &&
      typeof window.productBuilder.validateFinalizeReady === 'function' &&
      window.productBuilder.validateFinalizeReady.bind(window.productBuilder));
  if (typeof validate === 'function' && !validate(stateSlice)) {
    blockFinalizeWithHintAndJump(pb, ctx);
    return false;
  }
  return true;
}

/**
 * Reliable footer nav sync — immediate + microtask + rAF after field / validation changes.
 */
export function scheduleFooterNavSync() {
  const run = () => {
    if (footerNavSyncCoordinator) {
      footerNavSyncCoordinator.checkStepValidity();
      return;
    }
    if (typeof window !== 'undefined' && typeof window.productBuilder?.checkStepValidity === 'function') {
      window.productBuilder.checkStepValidity();
    }
  };
  run();
  if (typeof queueMicrotask === 'function') queueMicrotask(run);
  if (typeof requestAnimationFrame === 'function') requestAnimationFrame(run);
}

function resolveFlowFields(pb, stateSlice, mode, builderData) {
  const isMain = mode === FIELD_MODES.MAIN;
  const data =
    builderData ?? (typeof window !== 'undefined' ? window.BUILDER_DATA : null);
  if (isMain && data?.shopifyBundle === true && Array.isArray(data.shopifyBundleMergedPersonalization)) {
    return data.shopifyBundleMergedPersonalization;
  }
  const item = isMain ? data?.mainProduct || {} : resolveSubFlowItem(stateSlice);
  if (typeof pb?.getPersonalizationFields === 'function') {
    return pb.getPersonalizationFields(item) || [];
  }
  const fields = item?.config || item?.personalization || [];
  return Array.isArray(fields) ? fields : [];
}

function resolveFlowContainerSelector(mode) {
  return mode === FIELD_MODES.SUB ? '#sub-flow-container' : '#main-flow-container';
}

function resolveReviewAcknowledged(fieldLabel, wrap) {
  if (typeof window === 'undefined' || !window.PBAiReviewHop?.isReviewAcknowledged) return false;
  const builderState = window.builderState;
  const scopeId =
    typeof window.PBAiReviewHop.resolveActiveReviewScopeId === 'function'
      ? window.PBAiReviewHop.resolveActiveReviewScopeId(builderState)
      : undefined;
  // Cart edit: DOM wrap can lag state on mobile — fingerprint from state slice, not stale wrap.
  const stateOverride =
    builderState?.cartEdit || !wrap || !wrap._pbAiState ? undefined : wrap._pbAiState;
  return window.PBAiReviewHop.isReviewAcknowledged(
    builderState,
    fieldLabel,
    stateOverride,
    scopeId
  );
}

function resolveReviewAcknowledgedForField(field, wrap, builderData) {
  const keys = wrap
    ? [wrap.dataset?.label || field?.label || '']
    : resolveFieldIdentityKeys(field, builderData);
  for (let i = 0; i < keys.length; i += 1) {
    if (resolveReviewAcknowledged(keys[i], wrap)) return true;
  }
  return false;
}

function findAiWrapForField(field, containerSelector, builderData) {
  if (typeof document === 'undefined' || !field) return null;
  const root = document.querySelector(containerSelector);
  if (!root) return null;
  const keys = resolveFieldIdentityKeys(field, builderData);
  return findDomNodeForFieldIdentityKeys(root, '.pb-ai-image-wrap', keys);
}

function findAiWrapForLabel(containerSelector, label) {
  if (typeof document === 'undefined' || !label) return null;
  const root = document.querySelector(containerSelector);
  if (!root) return null;
  return findDomNodeForFieldIdentityKeys(root, '.pb-ai-image-wrap', [label]);
}

function findStepPageForField(field, containerSelector, builderData) {
  if (typeof document === 'undefined' || !field) return null;
  const root = document.querySelector(containerSelector);
  if (!root) return null;
  if (typeof root.querySelector === 'function') {
    const fromSharedLookup = findStepElementForField(root, field);
    if (fromSharedLookup) return fromSharedLookup;
  }
  const keys = resolveFieldIdentityKeys(field, builderData);
  const mounts = root.querySelectorAll('[data-pb-field-mount]');
  for (let i = 0; i < mounts.length; i += 1) {
    const mountField = parseMountField(mounts[i]);
    if (!mountField) continue;
    if (mountField.label === field.label) {
      return mounts[i].closest('.pb-step');
    }
    if (keys.includes(mountField.label)) {
      return mounts[i].closest('.pb-step');
    }
  }
  if (isAiImageField(field)) {
    const wrap = findAiWrapForField(field, containerSelector, builderData);
    return wrap ? wrap.closest('.pb-step') : null;
  }
  if (isAiTextField(field)) {
    const wrap = findDomNodeForFieldIdentityKeys(root, '.pb-ai-text-wrap', keys);
    return wrap ? wrap.closest('.pb-step') : null;
  }
  if (isUploadField(field) || isAudioField(field)) {
    const selector = isAudioField(field) ? '.pb-audio-wrap' : '.pb-upload-wrap';
    const wrap = findDomNodeForFieldIdentityKeys(root, selector, keys);
    if (wrap && !wrap.closest('.pb-ai-image-wrap')) {
      return wrap.closest('.pb-step');
    }
  }
  return null;
}

function resolveAiReadinessForField(field, containerSelector, builderData, builderState) {
  const wrap = findAiWrapForField(field, containerSelector, builderData);
  if (wrap) {
    const reviewAcknowledged = resolveReviewAcknowledgedForField(field, wrap, builderData);
    if (typeof window !== 'undefined' && window.PBAiImage?.getFieldReadiness) {
      return window.PBAiImage.getFieldReadiness(wrap, { reviewAcknowledged });
    }
    return getAiImageFieldReadiness(wrap, { reviewAcknowledged });
  }
  const saved = resolveAiImageSavedEntry(field, builderState, builderData);
  const required = fieldIsRequired(field);
  if (saved) {
    const reviewAcknowledged = resolveReviewAcknowledgedForField(field, null, builderData);
    const finalizeReady = resolveAiFinalizeReadyForSavedField(field, saved, reviewAcknowledged);
    return {
      stepNavigable: true,
      finalizeReady,
      jobStatus: saved.jobStatus || 'idle',
      characterReady: !!(saved.characterUrls || []).length,
      required,
    };
  }
  if (!required) {
    return { stepNavigable: true, finalizeReady: true, jobStatus: 'idle', characterReady: false, required: false };
  }
  const selections =
    builderState?.mode === 'SUB' ? builderState.sub?.selections : builderState?.main?.selections;
  const lookup = resolveFieldSelectionLookup(field, selections || {}, builderData);
  if (lookup.hasKey && selectionValueHasAnswer(lookup.value)) {
    return { stepNavigable: true, finalizeReady: true, jobStatus: 'idle', characterReady: false, required };
  }
  return {
    stepNavigable: false,
    finalizeReady: false,
    jobStatus: 'idle',
    characterReady: false,
    required,
  };
}

function resolveAiTextReadinessForField(field, containerSelector, builderData, builderState) {
  const keys = resolveFieldIdentityKeys(field, builderData);
  const root = typeof document !== 'undefined' ? document.querySelector(containerSelector) : null;
  const wrap = findDomNodeForFieldIdentityKeys(root, '.pb-ai-text-wrap', keys);
  if (wrap) {
    if (typeof window !== 'undefined' && window.PBAiText?.getFieldReadiness) {
      return window.PBAiText.getFieldReadiness(wrap);
    }
    return getAiTextFieldReadiness(wrap);
  }
  const bucket = builderState?.mode === 'SUB' && builderState.sub?.isQueueItem
    ? builderState.sub : builderState;
  const byLabel = bucket?.aiTextByLabel?.byLabel || {};
  const saved = keys.map((key) => byLabel[key]).find(Boolean);
  const snapshot = saved && !saved.jobStatus ? { ...saved, jobStatus: 'complete' } : saved;
  return computeAiTextReadiness(snapshot, parseAiTextFieldSettings(field?.settings), {
    reviewAcknowledged: !!saved?.reviewAcked,
  });
}

/**
 * @param {object} deps
 * @param {object} deps.pb — productBuilder instance
 * @param {() => object} [deps.getRegistry]
 */
export function createValidationCoordinator(deps) {
  const pb = deps.pb || deps;

  function parseSettings(raw) {
    return typeof pb.parseSettings === 'function' ? pb.parseSettings(raw) : {};
  }

  function validateButtonGrid(grid, settings = null) {
    const s = settings || parseSettings(grid?.dataset?.settings || '');
    return validateChoiceGrid(grid, s);
  }

  function validateVariantGroups(stepPage) {
    if (!stepPage || typeof stepPage.querySelectorAll !== 'function') return true;
    let allValid = true;
    stepPage.querySelectorAll('.variant-slider-track').forEach(function (track) {
      const isRequired = track.dataset.required === 'true';
      if (isRequired && !track.querySelector('.active')) allValid = false;
    });
    return allValid;
  }

  function validateStepLegacy(stepPage) {
    if (!stepPage || typeof stepPage.querySelector !== 'function') return true;
    if (stepPage.dataset.customField === 'true' || stepPage.querySelector('.pb-custom-inputs')) {
      return window.PBToolkit ? window.PBToolkit.validateStep(stepPage) : true;
    }
    if (
      window.PBAiImage &&
      typeof window.PBAiImage.validateStep === 'function' &&
      (stepPage.dataset.aiImage === 'true' || stepPage.querySelector('.pb-ai-image-wrap'))
    ) {
      if (!window.PBAiImage.validateStep(stepPage)) return false;
    }
    if (
      window.PBAiText &&
      typeof window.PBAiText.validateStep === 'function' &&
      (stepPage.dataset.aiText === 'true' || stepPage.querySelector('.pb-ai-text-wrap'))
    ) {
      if (!window.PBAiText.validateStep(stepPage)) return false;
    }
    if (
      window.PBDesignLibrary &&
      typeof window.PBDesignLibrary.validateStep === 'function' &&
      (stepPage.dataset.designLibrary === 'true' || stepPage.querySelector('.pb-dl-wrap'))
    ) {
      if (!window.PBDesignLibrary.validateStep(stepPage)) return false;
    }

    let allValid = true;
    const subSettingsRaw =
      typeof pb.getSubStepSettingsRaw === 'function' ? pb.getSubStepSettingsRaw(stepPage) : null;
    const subSettings = subSettingsRaw != null ? parseSettings(subSettingsRaw) : null;
    if (
      typeof window !== 'undefined' &&
      window.builderState?.mode === 'SUB' &&
      typeof pb.getSubStepEntry === 'function' &&
      pb.getSubStepEntry(stepPage)?.type === 'config' &&
      !subSettingsRaw
    ) {
      if (typeof pb.warnMissingSubSettings === 'function') pb.warnMissingSubSettings();
    }

    stepPage.querySelectorAll('.builder-input').forEach(function (input) {
      const settings = subSettings || parseSettings(input.dataset.settings || '');
      if (settings.uppercase) input.value = input.value.toUpperCase();
      const isValid =
        typeof pb.validateTextInput === 'function' ? pb.validateTextInput(input, settings) : true;
      if (!isValid) allValid = false;
    });

    stepPage.querySelectorAll('.builder-required').forEach(function (i) {
      if (i.classList.contains('pb-lt-audio-input')) return;
      if (i.classList.contains('pb-dl-required')) return;
      const val = (i.value || '').trim();
      if (i.dataset.required === 'true' && (i.dataset.valid === 'false' || !val)) allValid = false;
    });

    stepPage.querySelectorAll('.btn-grid').forEach(function (grid) {
      const gridValid = validateButtonGrid(grid, subSettings || null);
      if (!gridValid) allValid = false;
    });

    if (!validateVariantGroups(stepPage)) allValid = false;
    return allValid;
  }

  function buildRegistryCtx(stepPage, field, stateSlice, mode) {
    const isMain = mode === FIELD_MODES.MAIN;
    const item = isMain
      ? typeof window !== 'undefined'
        ? window.BUILDER_DATA?.mainProduct
        : {}
      : resolveSubFlowItem(stateSlice);
    if (isMain) {
      return buildFieldCtx(mode, field, stateSlice, { item, pb, mode });
    }
    return buildSubFieldCtx(field, item || {}, stateSlice, pb);
  }

  function validateActiveStepRegistry(stepPage, stateSlice, mode) {
    const registry = typeof deps.getRegistry === 'function' ? deps.getRegistry() : getDefaultFieldRegistry();
    const entry = typeof pb.getSubStepEntry === 'function' ? pb.getSubStepEntry(stepPage) : null;

    if (entry?.type === 'config' && entry.field) {
      const ctx = buildRegistryCtx(stepPage, entry.field, stateSlice, mode);
      return registry.validateStep(stepPage, ctx);
    }
    if (entry?.type === 'variant' && entry.option) {
      const variantField = buildVariantFieldFromOption(entry.option);
      const ctx = buildRegistryCtx(stepPage, variantField, stateSlice, mode);
      return registry.validateStep(stepPage, ctx);
    }
    if (stepPage?.dataset?.topicMulti === 'true') {
      const field = { type: 'TopicMulti', label: stepPage.dataset.topicLabel || '' };
      const ctx = buildRegistryCtx(stepPage, field, stateSlice, mode);
      return registry.validateStep(stepPage, ctx);
    }
    if (stepPage?.dataset?.topicParent) {
      return validateTopicAnswerStep(stepPage);
    }

    const mountEl = stepPage?.querySelector('[data-pb-field-mount]');
    if (mountEl) {
      const field = parseMountField(mountEl);
      if (field) {
        const ctx = buildRegistryCtx(stepPage, field, stateSlice, mode);
        return registry.validateStep(stepPage, ctx);
      }
    }

    if (stepPage?.querySelector('.variant-btn, .variant-slider-track')) {
      const variantField = buildVariantFieldFromOption({ name: '', values: [] });
      const ctx = buildRegistryCtx(stepPage, variantField, stateSlice, mode);
      if (!registry.validateStep(stepPage, ctx)) return false;
      return validateVariantGroups(stepPage);
    }

    return true;
  }

  function validateActiveStep(ctx) {
    const builderState = typeof window !== 'undefined' ? window.builderState : null;
    const stateSlice = ctx || (builderState?.mode === 'SUB' ? builderState?.sub : builderState?.main);
    return resolveStepReadiness(builderState, stateSlice, {
      pb,
      validateStep,
      scope: READINESS_SCOPE.ACTIVE,
    }).ready;
  }

  function validateFinalizeReady(ctx) {
    const builderState = typeof window !== 'undefined' ? window.builderState : null;
    const stateSlice = ctx || (builderState?.mode === 'SUB' ? builderState?.sub : builderState?.main);
    return resolveStepReadiness(builderState, stateSlice, {
      pb,
      validateStep,
      scope: READINESS_SCOPE.FULL,
    }).ready;
  }

  function validateStep(stepPage) {
    if (!stepPage) return true;
    if (isUnifiedFieldEngineEnabled()) {
      const builderState = typeof window !== 'undefined' ? window.builderState : null;
      const stateSlice =
        builderState?.mode === 'SUB' ? builderState?.sub : builderState?.main;
      const mode =
        builderState?.mode === 'SUB' ? FIELD_MODES.SUB : FIELD_MODES.MAIN;
      return validateActiveStepRegistry(stepPage, stateSlice, mode);
    }
    return validateStepLegacy(stepPage);
  }

  let checkingStepValidity = false;
  let checkStepValidityPending = false;

  function checkStepValidity() {
    if (checkingStepValidity) {
      checkStepValidityPending = true;
      return;
    }
    checkingStepValidity = true;
    try {
      const builderState = typeof window !== 'undefined' ? window.builderState : null;
      const ctx =
        builderState?.mode === 'SUB' ? builderState?.sub : builderState?.main;
      const onLastStep = ctx && ctx.maxSteps && ctx.step >= ctx.maxSteps;
      const stepValid = onLastStep ? validateFinalizeReady(ctx) : validateActiveStep(ctx);
      let doorwayBlocked = false;
      if (
        builderState?.mode === 'MAIN' &&
        ctx &&
        typeof blocksForwardToOffersAddonsDoorway === 'function'
      ) {
        doorwayBlocked = blocksForwardToOffersAddonsDoorway(ctx.step, builderState, {
          data: typeof window !== 'undefined' ? window.BUILDER_DATA : null,
          deps: {},
          navContext:
            typeof window !== 'undefined'
              ? resolveNavContextFromBuilderState(
                  builderState,
                  window.BUILDER_DATA,
                  builderState._pbCartEditCart
                )
              : null,
        });
      }
      const navContext =
        typeof window !== 'undefined'
          ? resolveNavContextFromBuilderState(
              builderState,
              window.BUILDER_DATA,
              builderState?._pbCartEditCart
            )
          : null;
      const baseNavOpts = {
        currentStep: ctx?.step,
        data: typeof window !== 'undefined' ? window.BUILDER_DATA : null,
        deps: {},
        navContext,
      };
      const forwardNavOpts = {
        ...baseNavOpts,
        delta: 1,
        navIntent: classifyNavigationIntent(builderState, { ...baseNavOpts, delta: 1 }),
      };
      const backNavOpts = {
        ...baseNavOpts,
        delta: -1,
        navIntent: classifyNavigationIntent(builderState, { ...baseNavOpts, delta: -1 }),
      };
      const forwardFlowBlocked = isRecordingNavBlocked(forwardNavOpts);
      const backFlowBlocked = isRecordingNavBlocked(backNavOpts);
      const nextEnabled = stepValid && !doorwayBlocked && !forwardFlowBlocked;
      const backEnabled = !backFlowBlocked;
      const recordingBlocked = forwardFlowBlocked || backFlowBlocked;
      const recordingMessage = recordingBlocked
        ? backFlowBlocked && !forwardFlowBlocked
          ? getSubLeaveBlockReason().message
          : getRecordingNavBlockMessage(forwardFlowBlocked ? forwardNavOpts : backNavOpts)
        : undefined;
      const nextBtn =
        builderState?.mode === 'MAIN'
          ? document.getElementById('nextBtn')
          : document.getElementById('subNextBtn');
      const backBtn =
        builderState?.mode === 'MAIN'
          ? document.getElementById('backBtn')
          : document.getElementById('subBackBtn');

      if (recordingBlocked) {
        syncStartFreshAffordance(false);
        applyFooterNavButtonState(nextBtn, {
          enabled: nextEnabled,
          title: !nextEnabled ? recordingMessage : undefined,
          lockKind: FOOTER_NAV_LOCK.RECORDING,
        });
        applyFooterNavButtonState(backBtn, {
          enabled: backEnabled,
          title: !backEnabled ? recordingMessage : undefined,
          lockKind: FOOTER_NAV_LOCK.RECORDING,
        });
      } else {
        const finalizeBlocked = onLastStep && !stepValid;
        let finalizeBlockedBy = null;
        if (finalizeBlocked) {
          const finalizeReadiness = resolveFinalizeReadiness(builderState, ctx, {
            pb,
            validateStep,
          });
          finalizeBlockedBy = finalizeReadiness.blockedBy;
        }
        const finalizeHint = finalizeBlocked ? getFinalizeBlockMessage(builderState) : '';
        if (finalizeBlocked && finalizeHint) {
          showNavBlockHint(finalizeHint);
        } else {
          clearNavBlockHint();
        }
        const showStartFresh =
          finalizeBlocked &&
          builderState?.mode === 'MAIN' &&
          !builderState?.cartEdit &&
          isBrokenSessionFinalizeBlocker(finalizeBlockedBy);
        syncStartFreshAffordance(showStartFresh);
        applyFooterNavButtonState(nextBtn, {
          enabled: nextEnabled,
          title: doorwayBlocked
            ? getOffersDoorwayBlockReason(builderState, { includeDom: false }).message
            : finalizeBlocked && finalizeHint
              ? finalizeHint
              : !stepValid
                ? 'Complete this step to continue'
                : undefined,
          lockKind: FOOTER_NAV_LOCK.VALIDATION,
        });
        applyFooterNavButtonState(backBtn, { enabled: backEnabled });
      }

      if (
        typeof window !== 'undefined' &&
        window.productBuilder &&
        typeof window.productBuilder.refreshAiReviewChrome === 'function'
      ) {
        window.productBuilder.refreshAiReviewChrome({ footer: onLastStep });
      }
    } finally {
      checkingStepValidity = false;
      if (checkStepValidityPending) {
        checkStepValidityPending = false;
        const rerun =
          typeof queueMicrotask === 'function'
            ? queueMicrotask
            : (fn) => {
                setTimeout(fn, 0);
              };
        rerun(() => checkStepValidity());
      }
    }
  }

  return {
    validateActiveStep,
    validateFinalizeReady,
    validateButtonGrid,
    validateVariantGroups,
    validateStep,
    validateStepLegacy,
    checkStepValidity,
  };
}

function attachToWindow() {
  if (typeof window === 'undefined') return;
  window.PBValidation = {
    createValidationCoordinator,
    scheduleFooterNavSync,
    applyFooterNavButtonState,
    resolveActiveModalFooter,
    showNavBlockHint,
    clearNavBlockHint,
    syncStartFreshAffordance,
    getFinalizeBlockMessage,
    resolveStepReadiness,
    resolveFinalizeReadiness,
    getFinishDiagnosticSnapshot,
    attemptFinalize,
    blockFinalizeWithHintAndJump,
    resolveBlockingStepIndexForFinalize,
    isBrokenSessionFinalizeBlocker,
    isRecordingNavBlocked,
    getRecordingNavBlockMessage,
    FOOTER_NAV_LOCK,
  };
  window.PBFinalizeGate = {
    attempt: attemptFinalize,
    getBlockMessage: getFinalizeBlockMessage,
    blockWithHint: function blockWithHint() {
      blockFinalizeWithHintAndJump(
        typeof window !== 'undefined' ? window.productBuilder : null
      );
    },
  };
  window.PBFooterNav = window.PBFooterNav || {};
  window.PBFooterNav.scheduleSync = scheduleFooterNavSync;
  window.PBFooterNav.applyButtonState = applyFooterNavButtonState;
  window.PBFooterNav.showBlockHint = showNavBlockHint;
  window.PBFooterNav.clearBlockHint = clearNavBlockHint;
  window.PBFinishDiagnostics = {
    getSnapshot() {
      return getFinishDiagnosticSnapshot();
    },
  };
}

function install(pb) {
  if (!pb || !window.PBValidation) return false;
  if (pb._pbValidationInstalled) return true;
  const coordinator = createValidationCoordinator({ pb });
  footerNavSyncCoordinator = coordinator;
  pb._pbValidationInstalled = true;
  pb._pbValidation = coordinator;
  pb.validateActiveStep = function (ctx) {
    return coordinator.validateActiveStep(ctx);
  };
  pb.validateFinalizeReady = function (ctx) {
    return coordinator.validateFinalizeReady(ctx);
  };
  pb.validateButtonGrid = function (grid, settings) {
    return coordinator.validateButtonGrid(grid, settings);
  };
  pb.validateVariantGroups = function (stepPage) {
    return coordinator.validateVariantGroups(stepPage);
  };
  pb.validateStep = function (stepPage) {
    return coordinator.validateStep(stepPage);
  };
  pb.checkStepValidity = function () {
    return coordinator.checkStepValidity();
  };
  pb.scheduleFooterNavSync = scheduleFooterNavSync;
  return true;
}

function tryInstall() {
  if (typeof window === 'undefined' || !window.productBuilder) return false;
  return install(window.productBuilder);
}

attachToWindow();

if (typeof window !== 'undefined') {
  window.__pbEnsureValidationCoordinator = install;
  if (!tryInstall()) {
    document.addEventListener('DOMContentLoaded', tryInstall);
    document.addEventListener('builder:ready', tryInstall);
  }
}
